import { FC, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { EmissionsByMonthResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { seriesPrimary, seriesSecondary, chartChrome } from '../../utils/chartColors';
import { formatCompact, monthAbbreviations } from '../../utils/localeFormat';
import { lastTwelveMonths, MONTH_WINDOW } from './monthWindow';
import './EmissionsByMonthChart.scss';

interface EmissionsByMonthChartProps {
  data: EmissionsByMonthResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const EmissionsByMonthChart: FC<EmissionsByMonthChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  /**
   * Whether the selection held more months than the chart draws. Needed in the
   * render body, not just inside the chart effect, because a narrowed chart has
   * to say so — silently showing twelve of thirty-six months would read as the
   * whole selection.
   */
  const { truncated } = useMemo(() => lastTwelveMonths(data?.data), [data]);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    // One year at a time. The axis labels carry no year, so a longer range drew
    // jan..dec once per year with no way to tell the repeats apart.
    const { months: windowed } = lastTwelveMonths(data.data);
    const monthNames = monthAbbreviations(lng);
    const months = windowed.map((d) => {
      const monthIndex = parseInt(d.month.split('-')[1], 10) - 1;
      return monthNames[monthIndex] || d.month;
    });
    const currentValues = windowed.map((d) => d.currentEmissionsKg);

    const primary = seriesPrimary();
    const secondary = seriesSecondary();
    const chrome = chartChrome();

    const series: echarts.LineSeriesOption[] = [
      {
        type: 'line',
        step: 'end',
        name: String(t('cpi.analytics.currentYear')),
        data: currentValues,
        lineStyle: { color: primary, width: 2.5 },
        itemStyle: { color: primary },
        showSymbol: false,
      },
    ];

    if (data.compareWithPreviousYear) {
      const previousValues = windowed.map((d) => d.previousEmissionsKg ?? 0);
      series.push({
        type: 'line',
        step: 'end',
        name: String(t('cpi.analytics.previousYear')),
        data: previousValues,
        lineStyle: { color: secondary, width: 2.5 },
        itemStyle: { color: secondary },
        showSymbol: false,
      });
    }

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis' },
      legend: data.compareWithPreviousYear
        ? { bottom: 0, textStyle: { fontSize: 12 } }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: data.compareWithPreviousYear ? '15%' : '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { fontSize: 12, color: chrome.muted, rotate: 45 },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.emissionsKg')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { formatter: (v: number) => formatCompact(v, lng), color: chrome.muted },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { type: 'dotted', color: chrome.grid } },
      },
      series,
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, t, lng]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="emissions-by-month-chart">
        <h3 className="emissions-by-month-chart__title">{t('cpi.analytics.emissionsByMonth')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="emissions-by-month-chart emissions-by-month-chart--loading">
        <h3 className="emissions-by-month-chart__title">{t('cpi.analytics.emissionsByMonth')}</h3>
        <div className="emissions-by-month-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="emissions-by-month-chart">
        <h3 className="emissions-by-month-chart__title">{t('cpi.analytics.emissionsByMonth')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="emissions-by-month-chart">
      <h3 className="emissions-by-month-chart__title">{t('cpi.analytics.emissionsByMonth')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
      {truncated && (
        <p className="emissions-by-month-chart__notice">
          {t('cpi.analytics.monthWindowNotice', {
            months: MONTH_WINDOW,
          })}
        </p>
      )}
    </div>
  );
};

export default EmissionsByMonthChart;
